import { useRef, useCallback, useEffect } from 'react';

export function useThrottle<T extends (...args: any[]) => any>(
    callback: T,
    delayMs: number,
): T {
    const lastRunRef = useRef<number>(0);
    const timeoutRef = useRef<NodeJS.Timeout | null>(null);
    const callbackRef = useRef(callback);

    useEffect(() => {
        callbackRef.current = callback;
        return () => {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }
        };
    }, [callback]);

    return useCallback(
        ((...args: Parameters<T>) => {
            const now = Date.now();
            const timeSinceLastRun = now - lastRunRef.current;

            if (timeSinceLastRun >= delayMs) {
                lastRunRef.current = now;
                callbackRef.current(...args);
            } else {
                if (timeoutRef.current) {
                    clearTimeout(timeoutRef.current);
                }

                timeoutRef.current = setTimeout(() => {
                    lastRunRef.current = Date.now();
                    callbackRef.current(...args);
                }, delayMs - timeSinceLastRun);
            }
        }) as T,
        [delayMs],
    );
}
